import React, { useState, useRef, useEffect } from "react"

const VoiceRecorder = ({ onRecordingComplete, disabled = false }) => {
  const [isRecording, setIsRecording] = useState(false)
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [hasPermission, setHasPermission] = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const streamRef = useRef(null)
  const durationIntervalRef = useRef(null)
  const startTimeRef = useRef(0)

  // Check microphone permission on mount
  useEffect(() => {
    checkMicrophonePermission()
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording()
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
      }
    }
  }, [])

  const checkMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      setHasPermission(true)
      // Stop the test stream immediately
      stream.getTracks().forEach(track => track.stop())
    } catch (error) {
      console.log("Microphone permission denied:", error)
      setHasPermission(false)
    }
  }

  const startRecording = async () => {
    if (disabled || isRecording || isUploading) return

    try {
      console.log("🎤 Starting voice recording...")

      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      })

      streamRef.current = stream
      audioChunksRef.current = []
      startTimeRef.current = Date.now()

      // Create MediaRecorder with fallback mime types
      let mimeType = "audio/webm;codecs=opus"
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "audio/webm"
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "audio/mp4"
        }
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType
      })

      mediaRecorderRef.current = mediaRecorder

      // Handle data available
      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
          console.log("🎤 Audio chunk received:", event.data.size, "bytes")
        }
      }

      // Handle recording stop
      mediaRecorder.onstop = () => {
        console.log("🎤 Recording stopped, processing audio...")

        const audioBlob = new Blob(audioChunksRef.current, {
          type: mimeType
        })

        // Calculate actual duration from start time
        const endTime = Date.now()
        const timestampDuration = Math.max(
          1,
          Math.round((endTime - startTimeRef.current) / 1000)
        )
        const actualDuration =
          timestampDuration > 0
            ? timestampDuration
            : Math.max(1, recordingDuration)

        console.log("🎤 Audio blob created:", {
          size: audioBlob.size,
          duration: actualDuration,
          recordingDuration: recordingDuration,
          type: audioBlob.type,
          chunks: audioChunksRef.current.length,
          startTime: startTimeRef.current,
          endTime: endTime
        })

        // Call the completion callback with actual duration
        onRecordingComplete(audioBlob, actualDuration)

        // Cleanup
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop())
          streamRef.current = null
        }

        setIsRecording(false)
        setRecordingDuration(0)
      }

      // Start recording
      mediaRecorder.start(100) // Collect data every 100ms
      setIsRecording(true)
      setRecordingDuration(0)

      // Start duration counter
      durationIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1)
      }, 1000)

      console.log("🎤 Recording started successfully with mimeType:", mimeType)
    } catch (error) {
      console.error("❌ Error starting recording:", error)
      alert(
        "Failed to access microphone. Please check your permissions and try again."
      )
      setIsRecording(false)
    }
  }

  const stopRecording = () => {
    if (!isRecording || !mediaRecorderRef.current) return

    console.log("🎤 Stopping recording...")
    console.log("🎤 Current recording duration:", recordingDuration, "seconds")

    // Stop the recording
    mediaRecorderRef.current.stop()

    // Clear duration interval
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current)
      durationIntervalRef.current = null
    }
  }

  const formatDuration = seconds => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  const handleMouseDown = () => {
    if (!disabled && hasPermission && !isUploading) {
      startRecording()
    }
  }

  const handleMouseUp = () => {
    if (isRecording) {
      stopRecording()
    }
  }

  const handleMouseLeave = () => {
    if (isRecording) {
      stopRecording()
    }
  }

  // Show permission request if needed
  if (hasPermission === false) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-2 bg-[#fff3cd] rounded-full border border-[#ffeaa7] text-[#856404] text-xs"
      >
        <span>🎤 Access denied</span>
        <button
          onClick={checkMicrophonePermission}
          className="underline cursor-pointer font-medium hover:text-[#533f03]"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Recording Duration Indicator */}
      {isRecording && (
        <div
          className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-[#333] text-white text-xs px-2 py-1 rounded whitespace-nowrap shadow-sm z-10"
        >
          {formatDuration(recordingDuration)}
        </div>
      )}

      <button
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={(e) => {
          e.preventDefault() // Prevent scrolling while recording
          handleMouseDown()
        }}
        onTouchEnd={(e) => {
          e.preventDefault()
          handleMouseUp()
        }}
        disabled={disabled || hasPermission === false}
        className={`
          w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm select-none
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${isRecording 
            ? 'bg-[#dc3545] text-white scale-110 shadow-md animate-pulse ring-4 ring-[#dc3545]/30' 
            : 'bg-[#f8f9fa] text-[#6c757d] hover:bg-[#e9ecef] hover:scale-105'
          }
        `}
        title="Hold to record"
      >
        <span className="text-xl">
          {isRecording ? "⏹" : "🎤"}
        </span>
      </button>
    </div>
  )
}

export default VoiceRecorder

